import { useEffect, useRef, useState, type ReactElement } from "react";
import { Link, useLocation } from "react-router";
import { PublicNavLink } from "@/app/layout/public-nav-link";
import {
  PUBLIC_HEADER_MENU,
  isActivePublicPath,
  type PublicHeaderGroup,
} from "@/app/layout/public-navigation";
import { ClmIcon } from "@/shared/components/icons/icon-map";
import { Button } from "@/shared/components/ui";
import { cn } from "@/shared/utils/cn";

// Keyboard focus ring that stays visible on the gradient header (white) and on the white panels.
const FOCUS_RING_ON_GRADIENT =
  "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white";
const FOCUS_RING_ON_WHITE =
  "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-clm-primary-blue";

function listId(group: PublicHeaderGroup): string {
  return `public-nav-${group.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-list`;
}

function isGroupActive(pathname: string, group: PublicHeaderGroup): boolean {
  return (
    isActivePublicPath(pathname, group.href) ||
    group.entries.some((entry) => isActivePublicPath(pathname, entry.href))
  );
}

// "page" on the group's own landing page, "true" while one of its child pages is current.
function groupCurrent(pathname: string, group: PublicHeaderGroup): "page" | "true" | undefined {
  if (isActivePublicPath(pathname, group.href)) {
    return "page";
  }
  return isGroupActive(pathname, group) ? "true" : undefined;
}

/**
 * Legacy header dropdowns for large viewports (disclosure navigation pattern): each top-level
 * label is a link to its landing page and a separate button discloses the group's links. Click or
 * keyboard only, never hover only; Escape returns focus to the button, and an outside click, focus
 * leaving the navigation or a route change (including browser Back/Forward) closes the open group.
 * The nav has no backing of its own: white text sits on the header gradient (centred, over its
 * middle; dark #16456B end on the left). Each group is a 31px pill (Tracker-UX Navigation Item "Focus", Figma node 249:11299) that
 * darkens with `bg-black/20` (Figma's white/15 fails contrast here) when hovered, open or current; the
 * current one adds a 2px white bottom border, which forced-colors mode keeps (a background it drops).
 */
export function PublicDesktopNav(): ReactElement {
  const { pathname } = useLocation();
  const [openLabel, setOpenLabel] = useState<string | null>(null);
  const [openPathname, setOpenPathname] = useState(pathname);
  const navRef = useRef<HTMLElement | null>(null);
  const toggleRefs = useRef(new Map<string, HTMLButtonElement>());

  if (openPathname !== pathname) {
    setOpenPathname(pathname);
    setOpenLabel(null);
  }

  useEffect(() => {
    if (openLabel === null) {
      return;
    }
    const closeWhenOutside = (event: Event): void => {
      if (event.target instanceof Node && !navRef.current?.contains(event.target)) {
        setOpenLabel(null);
      }
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        toggleRefs.current.get(openLabel)?.focus();
        setOpenLabel(null);
      }
    };
    document.addEventListener("pointerdown", closeWhenOutside);
    document.addEventListener("focusin", closeWhenOutside);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", closeWhenOutside);
      document.removeEventListener("focusin", closeWhenOutside);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [openLabel]);

  return (
    <nav ref={navRef} aria-label="Site navigation" className="hidden self-stretch lg:flex">
      <ul className="m-0 flex h-full list-none p-0">
        {PUBLIC_HEADER_MENU.map((group, index) => {
          const open = openLabel === group.label;
          const id = listId(group);
          return (
            <li key={group.label} className="relative flex">
              <div
                className={cn(
                  "flex h-[31px] self-center rounded-[3px] hover:bg-black/20",
                  (open || isGroupActive(pathname, group)) && "bg-black/20",
                  isGroupActive(pathname, group) && "border-b-2 border-white",
                )}
              >
                <Link
                  to={group.href}
                  aria-current={groupCurrent(pathname, group)}
                  className={cn(
                    "flex items-center pl-3 pr-1 text-[14px] font-semibold text-white no-underline hover:no-underline",
                    FOCUS_RING_ON_GRADIENT,
                  )}
                  onClick={() => setOpenLabel(null)}
                >
                  {group.label}
                </Link>
                <button
                  type="button"
                  ref={(element) => {
                    if (element) {
                      toggleRefs.current.set(group.label, element);
                    } else {
                      toggleRefs.current.delete(group.label);
                    }
                  }}
                  aria-label={`${group.label} menu`}
                  aria-expanded={open}
                  aria-controls={id}
                  className={cn(
                    "flex w-7 cursor-pointer items-center justify-center text-white",
                    FOCUS_RING_ON_GRADIENT,
                  )}
                  onClick={() => setOpenLabel(open ? null : group.label)}
                >
                  <ClmIcon name="chevron-down" decorative className="h-4 w-4" />
                </button>
              </div>
              <ul
                id={id}
                hidden={!open}
                className={cn(
                  "absolute top-full z-40 m-0 min-w-[240px] list-none rounded-b-md border border-t-0 border-clm-light-gray bg-white p-0 py-1 shadow-md",
                  index === PUBLIC_HEADER_MENU.length - 1 ? "right-0" : "left-0",
                )}
              >
                {group.entries.map((entry) => (
                  <li key={entry.href}>
                    <PublicNavLink
                      entry={entry}
                      current={isActivePublicPath(pathname, entry.href)}
                      className={cn(
                        "block px-4 py-2 text-sm font-medium no-underline hover:bg-muted hover:text-clm-primary-blue hover:no-underline",
                        isActivePublicPath(pathname, entry.href)
                          ? "text-clm-primary-blue"
                          : "text-clm-dark-gray",
                        FOCUS_RING_ON_WHITE,
                      )}
                      onClick={() => {
                        // An external link opens in a new tab and leaves this page where it is, so focus
                        // must not be lost with the closing list; an internal link navigates away.
                        if (entry.external) {
                          toggleRefs.current.get(group.label)?.focus();
                        }
                        setOpenLabel(null);
                      }}
                    />
                  </li>
                ))}
              </ul>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/**
 * The same links below the large breakpoint: a menu button in the header opens a panel under it
 * with every group expanded, so no nested disclosure is needed on a small screen. Escape and an
 * outside click close it; choosing a link or a route change (including browser Back/Forward)
 * closes it.
 */
export function PublicMobileMenu(): ReactElement {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [openPathname, setOpenPathname] = useState(pathname);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const toggleRef = useRef<HTMLButtonElement | null>(null);

  if (openPathname !== pathname) {
    setOpenPathname(pathname);
    setOpen(false);
  }

  useEffect(() => {
    if (!open) {
      return;
    }
    const closeWhenOutside = (event: Event): void => {
      if (event.target instanceof Node && !containerRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        toggleRef.current?.focus();
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", closeWhenOutside);
    document.addEventListener("focusin", closeWhenOutside);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", closeWhenOutside);
      document.removeEventListener("focusin", closeWhenOutside);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="lg:hidden">
      <Button
        ref={toggleRef}
        type="button"
        variant="outline"
        size="icon"
        className="border-white/60 bg-transparent text-white hover:border-white/60 hover:bg-black/20 hover:text-white"
        aria-label="Site menu"
        aria-expanded={open}
        aria-controls="public-site-menu"
        onClick={() => setOpen((current) => !current)}
      >
        <ClmIcon name={open ? "close" : "menu"} decorative />
      </Button>
      <nav
        id="public-site-menu"
        aria-label="Mobile site navigation"
        hidden={!open}
        className="absolute inset-x-0 top-full z-40 max-h-[calc(100dvh-54px)] overflow-y-auto border-b border-clm-light-gray bg-white text-clm-dark-gray shadow-md"
      >
        <ul className="clm-page-gutter m-0 list-none py-2">
          {PUBLIC_HEADER_MENU.map((group) => (
            <li key={group.label} className="py-1">
              <Link
                to={group.href}
                aria-current={groupCurrent(pathname, group)}
                className={cn(
                  "block rounded-md px-3 py-2.5 text-sm font-bold text-clm-primary-blue no-underline hover:bg-muted hover:no-underline",
                  FOCUS_RING_ON_WHITE,
                )}
                onClick={() => setOpen(false)}
              >
                {group.label}
              </Link>
              <ul className="m-0 list-none p-0 pl-3">
                {group.entries.map((entry) => (
                  <li key={entry.href}>
                    <PublicNavLink
                      entry={entry}
                      current={isActivePublicPath(pathname, entry.href)}
                      className={cn(
                        "block rounded-md px-3 py-2.5 text-sm font-medium no-underline hover:bg-muted hover:text-clm-primary-blue hover:no-underline",
                        isActivePublicPath(pathname, entry.href)
                          ? cn(
                              "bg-clm-primary-blue text-white hover:bg-clm-primary-blue hover:text-white",
                              FOCUS_RING_ON_GRADIENT,
                            )
                          : FOCUS_RING_ON_WHITE,
                      )}
                      onClick={() => {
                        if (entry.external) {
                          toggleRef.current?.focus();
                        }
                        setOpen(false);
                      }}
                    />
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
